import { Button, IconButton, useTheme } from '@mui/material';
import { DeleteIcon, DistributeIcon, DistributeIconButton, GridItem, Heading, ManualWrapper, PaletteIcon, RangeTextField, RangeValueHelperText, RangeWrapper, RowBorder, } from './RangeFormatting.styles';
import { defaultRangeValues, generateCustomGradientSteps, generateGradientSteps, getColorOptions, gradients, } from './RangeFormatting.utils';
import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { useGlobalization } from '../../contexts';
import { useDebouncedCallback } from '../../hooks/useDebouncedCallback';
import ColorPicker from '../ColorPicker';
import StylePicker from '../StylePicker/StylePicker';
const nextValueInRange = (ranges) => {
    const lastValue = ranges.at(-1)?.value || 0;
    const secondLastValue = ranges.at(-2)?.value || 0;
    const diff = Math.max(lastValue - secondLastValue, 5);
    return lastValue + diff;
};
const nextColor = (ranges, colors) => {
    const lastColor = ranges.at(-1)?.color || colors[0];
    const lastIndex = colors.indexOf(lastColor) + 1;
    return colors[lastIndex] || colors[0];
};
const colorRange = (ranges, gradientKey) => {
    if (!gradientKey)
        return;
    const gradientSteps = generateGradientSteps(gradientKey, ranges.length);
    return ranges.map((range, i) => ({ ...range, color: gradientSteps[i] }));
};
const addRowToRange = (ranges, colors, gradientKey) => {
    const newRow = {
        value: nextValueInRange(ranges),
        color: nextColor(ranges, colors),
        style: {},
    };
    const newRange = [...ranges, newRow];
    return colorRange(newRange, gradientKey) || newRange;
};
const distributeRange = (ranges) => {
    const start = ranges.at(0)?.value;
    const end = ranges.at(-1)?.value;
    if (start == null || end == null)
        return;
    const diff = (end - start) / (ranges.length - 1);
    return ranges.map((range, i) => ({ ...range, value: start + diff * i }));
};
const RangeFormatting = ({ variant = 'manual', defaultRanges, onRangeChange, }) => {
    const { t } = useGlobalization();
    const [ranges, setRanges] = useState(defaultRanges || defaultRangeValues);
    const [gradient, setGradient] = useState(undefined);
    const bouncedCallback = useDebouncedCallback(value => {
        onRangeChange?.(value);
    }, 300);
    const updateRanges = (range) => {
        setRanges(range);
        bouncedCallback(range);
    };
    const theme = useTheme();
    const colorOptions = getColorOptions(theme);
    const colors = useMemo(() => colorOptions.map(({ value }) => value), [colorOptions]);
    const addRow = () => {
        updateRanges(addRowToRange(ranges, colors, gradient));
    };
    const updateValue = (index) => (event) => {
        const value = +event.target.value;
        updateRanges(ranges.map((range, i) => i === index ? { ...range, value } : range));
    };
    const updateColor = (index) => (color) => {
        // if individual colour is set then disable colouring by gradient
        setGradient(undefined);
        updateRanges(ranges.map((range, i) => i === index ? { ...range, color } : range));
    };
    const updateStyle = (index) => (style) => {
        updateRanges(ranges.map((range, i) => i === index ? { ...range, style } : range));
    };
    const deleteRow = (index) => () => {
        const updatedRange = ranges.filter((range, i) => i !== index);
        updateRanges(colorRange(updatedRange, gradient) || updatedRange);
    };
    const onSelectGradient = (gradientKey) => {
        setGradient(gradientKey);
        const recolored = colorRange(ranges, gradientKey);
        if (recolored)
            updateRanges(recolored);
    };
    const onDistributeColors = () => {
        const start = ranges.at(0)?.color;
        const end = ranges.at(-1)?.color;
        if (start && end) {
            // if distribute colour is triggered set then disable colouring by gradient
            setGradient(undefined);
            const gradientSteps = generateCustomGradientSteps([start, end], ranges.length);
            updateRanges(ranges.map((range, i) => ({
                ...range,
                color: gradientSteps[i],
            })));
        }
    };
    const onDistributeRange = () => {
        const newRange = distributeRange(ranges);
        if (newRange)
            updateRanges(newRange);
    };
    useEffect(() => {
        if (defaultRanges) {
            updateRanges(defaultRanges);
        }
    }, [defaultRanges]);
    if (variant === 'manual')
        return (React.createElement(ManualWrapper, null,
            React.createElement(GridItem, { "$header": true },
                React.createElement(Heading, null, t('Cell Color'))),
            React.createElement(GridItem, { "$header": true },
                React.createElement(Heading, null, t('Text Style'))),
            React.createElement(RowBorder, { cols: 2 }),
            React.createElement(GridItem, null,
                React.createElement(ColorPicker, { name: "color", label: "", value: ranges[0].color, options: colorOptions.map(c => c.value), dataCy: "range-color-picker-button", onChange: updateColor(0), TextFieldProps: {
                        fullWidth: false,
                        size: 'small',
                        margin: 'none',
                    } })),
            React.createElement(GridItem, null,
                React.createElement(StylePicker, { value: ranges[0].style, onChange: updateStyle(0) })),
            React.createElement(RowBorder, { cols: 2 })));
    return (React.createElement(RangeWrapper, null,
        React.createElement(GridItem, { "$header": true },
            React.createElement(Heading, null, t('Range')),
            React.createElement(DistributeIconButton, { size: "small", title: t('Distribute range', {
                    context: 'Layers properties range component button',
                }), "data-cy": "distribute-range", onClick: onDistributeRange },
                React.createElement(DistributeIcon, null))),
        React.createElement(GridItem, { "$header": true },
            React.createElement(Heading, null, t('Color')),
            React.createElement(DistributeIconButton, { size: "small", title: t('Distribute colors', {
                    context: 'Layers properties range component button',
                }), "data-cy": "distribute-colors", onClick: onDistributeColors },
                React.createElement(DistributeIcon, null)),
            React.createElement(ColorPicker, { label: "", name: "color", options: gradients, onChange: onSelectGradient, value: gradient, "data-cy": "gradient-picker" },
                React.createElement(IconButton, { size: "small", title: t('Select gradient for range', {
                        context: 'Layers properties range component button',
                    }), color: gradient ? 'primary' : undefined },
                    React.createElement(PaletteIcon, null)))),
        React.createElement(GridItem, { "$header": true },
            React.createElement(Heading, null, t('Text Style'))),
        React.createElement(GridItem, { "$header": true }),
        React.createElement(RowBorder, { cols: 4 }),
        ranges.map(({ value, color, style }, i) => (React.createElement(Fragment, { key: i },
            React.createElement(GridItem, null,
                ">",
                React.createElement(RangeTextField, { value: value, onChange: updateValue(i), "data-cy": "range-color-value" })),
            React.createElement(GridItem, null,
                React.createElement(ColorPicker, { name: "color", label: "", value: color, options: colorOptions.map(c => c.value), dataCy: "range-color-picker-button", onChange: updateColor(i), TextFieldProps: {
                        fullWidth: false,
                        size: 'small',
                        margin: 'none',
                    } })),
            React.createElement(GridItem, null,
                React.createElement(StylePicker, { value: style, onChange: updateStyle(i) })),
            React.createElement(GridItem, { "$noPadding": true }, ranges.length > 2 && (React.createElement(IconButton, { size: "small", onClick: deleteRow(i), "data-cy": "delete-row", title: t('Delete row', {
                    context: 'Layers properties range component button',
                }) },
                React.createElement(DeleteIcon, null)))),
            value <= ranges[i - 1]?.value && (React.createElement(RangeValueHelperText, null, t('Range value should be higher than previous value', {
                context: 'Layer properties form field valiation message',
            }))),
            React.createElement(RowBorder, { cols: 4 })))),
        React.createElement(GridItem, null,
            React.createElement(Button, { size: "small", onClick: addRow }, t('Add row', {
                context: 'Add additional row in range list in layer properties form',
            })))));
};
export default RangeFormatting;
